import React, { useState, useEffect, useCallback } from "react";
import { authFetch, API_BASE } from "../api";
import "./PublishWizard.css";

function cmsHeaders(cmsAuth) {
  return {
    "Content-Type": "application/json",
    "x-videri-token": cmsAuth.token,
    "x-tenant": cmsAuth.tenant,
    "x-videri-env": cmsAuth.environment || "dev",
  };
}

const PUBLISH_SETTINGS_KEY = "forge-publish-settings";

function loadPublishSettings() {
  try {
    const saved = localStorage.getItem(PUBLISH_SETTINGS_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch { return {}; }
}

function savePublishSettings(settings) {
  try { localStorage.setItem(PUBLISH_SETTINGS_KEY, JSON.stringify(settings)); } catch { /* ok */ }
}

export default function PublishWizard({ integration, cmsAuth, onClose, onPublished }) {
  const [step, setStep] = useState(1); // 1: Workspace, 2: Devices, 3: Schedule, 4: Confirm
  const [loadingWs, setLoadingWs] = useState(false);  // Workspace loading (separate from device loading)
  const [loadingDev, setLoadingDev] = useState(false); // Device loading
  const [publishing, setPublishing] = useState(false); // Publish in progress
  const [error, setError] = useState(null);

  // Load saved publish settings
  const savedSettings = React.useMemo(() => loadPublishSettings(), []);

  // Step 1: Workspaces
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [wsSearch, setWsSearch] = useState("");

  // Step 3: Devices
  const [devices, setDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [devSearch, setDevSearch] = useState("");

  // Step 4: Schedule
  function toLocalDatetimeString(date) {
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }
  const [startTime, setStartTime] = useState(() => {
    const midnight = new Date();
    midnight.setHours(0, 0, 0, 0);
    return toLocalDatetimeString(midnight);
  });
  const [noEndDate, setNoEndDate] = useState(savedSettings.noEndDate !== undefined ? savedSettings.noEndDate : true);
  const [endTime, setEndTime] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return toLocalDatetimeString(d);
  });
  const [duration, setDuration] = useState(savedSettings.duration || 15);
  const [priority, setPriority] = useState(
    [100, 200, 300].includes(savedSettings.priority) ? savedSettings.priority : 100
  );

  // App Variables (key-value pairs passed as query params / intent extras)
  const [appVars, setAppVars] = useState(savedSettings.appVars || []);
  const [newVarName, setNewVarName] = useState("");
  const [newVarValue, setNewVarValue] = useState("");

  // Step 5: Publishing
  const [published, setPublished] = useState(false);

  // Fetch workspaces once on mount (not on every cmsAuth reference change)
  const wsLoadedRef = React.useRef(false);
  useEffect(() => {
    if (wsLoadedRef.current) return;
    wsLoadedRef.current = true;
    setLoadingWs(true);
    authFetch(`${API_BASE}/videri/workspaces`, { headers: cmsHeaders(cmsAuth) })
      .then((r) => r.json())
      .then((data) => {
        const ws = data.workspaces || [];
        setWorkspaces(ws);
        if (savedSettings.workspaceId) {
          const match = ws.find((w) => w.id === savedSettings.workspaceId);
          if (match) setSelectedWorkspace(match);
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoadingWs(false));
  }, []);
  // Fetch devices when workspace is selected and we enter step 2
  const lastFetchedWsRef = React.useRef(null);
  useEffect(() => {
    if (step !== 2 || !selectedWorkspace) return;
    // Only refetch if workspace changed
    if (lastFetchedWsRef.current === selectedWorkspace.id) return;
    lastFetchedWsRef.current = selectedWorkspace.id;

    setLoadingDev(true);
    setError(null);
    authFetch(
      `${API_BASE}/videri/devices?groupId=${selectedWorkspace.id}`,
      { headers: cmsHeaders(cmsAuth) }
    )
      .then((r) => r.json())
      .then((data) => {
        const devs = data.devices || [];
        setDevices(devs);
        // Auto-select previously used devices
        if (savedSettings.deviceIds && savedSettings.deviceIds.length > 0 && selectedDevices.length === 0) {
          const validIds = devs.map((d) => d.id || d.deviceId);
          const restored = savedSettings.deviceIds.filter((id) => validIds.includes(id));
          if (restored.length > 0) setSelectedDevices(restored);
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoadingDev(false));
  }, [step, selectedWorkspace]);
  const toggleDevice = (deviceId) => {
    setSelectedDevices((prev) =>
      prev.includes(deviceId) ? prev.filter((id) => id !== deviceId) : [...prev, deviceId]
    );
  };

  const selectAllDevices = () => {
    const filtered = filteredDevices();
    if (selectedDevices.length === filtered.length) {
      setSelectedDevices([]);
    } else {
      setSelectedDevices(filtered.map((d) => d.id || d.deviceId));
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    setError(null);
    try {
      const startISO = startTime + ":00.000Z";
      const endISO = noEndDate
        ? "2126-01-01T00:00:00.000Z"
        : endTime + ":00.000Z";

      // APK delivery via Universal Player — no build needed!
      // The player APK is already in the CMS. We just schedule it with app_url.
      const res = await authFetch(`${API_BASE}/apk/publish-player`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-videri-token": cmsAuth.token,
          "x-tenant": cmsAuth.tenant,
          "x-videri-env": cmsAuth.environment || "dev",
        },
        body: JSON.stringify({
          name: integration.name,
          appUrl: integration.url,
          groupId: selectedWorkspace.id,
          canvasIds: selectedDevices,
          startTime: startISO,
          endTime: endISO,
          durationMs: duration * 1000,
          priority,
          appVars: appVars.filter((v) => v.name && v.value),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.error === 'string' ? data.error : data.error?.message || "APK publish failed");

      setPublished(true);
      if (onPublished) onPublished(data);

      // Save settings for next time
      savePublishSettings({
        workspaceId: selectedWorkspace?.id,
        deviceIds: selectedDevices,
        duration,
        priority,
        noEndDate,
        appVars: appVars.filter((v) => v.name && v.value),
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setPublishing(false);
    }
  };

  const filteredWorkspaces = () =>
    workspaces.filter((w) =>
      (w.name || "").toLowerCase().includes(wsSearch.toLowerCase())
    );

  const filteredDevices = () =>
    devices.filter((d) =>
      (d.name || "").toLowerCase().includes(devSearch.toLowerCase())
    );

  const canNext = () => {
    if (step === 1) return !!selectedWorkspace;
    if (step === 2) return selectedDevices.length > 0;
    if (step === 3) return startTime && (noEndDate || endTime);
    return false;
  };

  return (
    <div className="pw-overlay">
      <div className="pw-modal">
        <div className="pw-header">
          <h2>Publish to Devices</h2>
          <button className="pw-close" onClick={onClose}>&times;</button>
        </div>

        {/* Step indicator */}
        <div className="pw-steps">
          {["Workspace", "Devices", "Schedule", "Confirm"].map((label, i) => (
            <div
              key={label}
              className={`pw-step ${step === i + 1 ? "active" : ""} ${step > i + 1 ? "done" : ""}`}
            >
              <span className="pw-step-num">{step > i + 1 ? "\u2713" : i + 1}</span>
              <span className="pw-step-label">{label}</span>
            </div>
          ))}
        </div>

        <div className="pw-body">
          {error && <div className="pw-error">{error}</div>}

          {/* Step 1: Workspace */}
          {step === 1 && (
            <div className="pw-section">
              <input
                className="pw-search"
                type="text"
                placeholder="Search workspaces..."
                value={wsSearch}
                onChange={(e) => setWsSearch(e.target.value)}
              />
              {loadingWs ? (
                <div className="pw-loading">Loading workspaces...</div>
              ) : (
                <div className="pw-list">
                  {filteredWorkspaces().map((ws) => (
                    <div
                      key={ws.id}
                      className={`pw-list-item ${selectedWorkspace?.id === ws.id ? "selected" : ""}`}
                      onClick={() => setSelectedWorkspace(ws)}
                      style={{ paddingLeft: `${14 + (ws.depth || 0) * 16}px` }}
                    >
                      {(ws.depth || 0) > 0 && <span className="pw-tree-indent">{"└"}</span>}
                      <span className="pw-item-name">{ws.name}</span>
                    </div>
                  ))}
                  {filteredWorkspaces().length === 0 && (
                    <div className="pw-empty">No workspaces found.</div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Devices */}
          {step === 2 && (
            <div className="pw-section">
              <div className="pw-device-bar">
                <input
                  className="pw-search"
                  type="text"
                  placeholder="Search devices..."
                  value={devSearch}
                  onChange={(e) => setDevSearch(e.target.value)}
                />
                <button className="pw-select-all" onClick={selectAllDevices}>
                  {selectedDevices.length === filteredDevices().length ? "Deselect All" : "Select All"}
                </button>
              </div>
              {loadingDev ? (
                <div className="pw-loading">Loading devices...</div>
              ) : (
                <div className="pw-list">
                  {filteredDevices().map((dev) => {
                    const devId = dev.id || dev.deviceId;
                    return (
                      <div
                        key={devId}
                        className={`pw-list-item device-item ${selectedDevices.includes(devId) ? "selected" : ""}`}
                        onClick={() => toggleDevice(devId)}
                      >
                        <input
                          type="checkbox"
                          checked={selectedDevices.includes(devId)}
                          readOnly
                        />
                        <span className={`pw-status-dot ${dev.online ? "online" : "offline"}`} />
                        <div className="pw-device-info">
                          <span className="pw-item-name">{dev.name}</span>
                          <span className="pw-item-desc">
                            {dev.productName}{dev.location ? ` · ${dev.location}` : ""}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  {filteredDevices().length === 0 && (
                    <div className="pw-empty">No devices found in this workspace.</div>
                  )}
                </div>
              )}
              <div className="pw-selected-count">
                {selectedDevices.length} device{selectedDevices.length !== 1 ? "s" : ""} selected
              </div>
            </div>
          )}

          {/* Step 3: Schedule */}
          {step === 3 && (
            <div className="pw-section">
              <div className="pw-schedule-presets">
                <span className="pw-preset-label">Quick schedule:</span>
                <button className="pw-preset" onClick={() => {
                  setNoEndDate(true);
                  const today = new Date();
                  setStartTime(toLocalDatetimeString(new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0)));
                }}>
                  Always on
                </button>
                <button className="pw-preset" onClick={() => {
                  const now = new Date();
                  const end = new Date();
                  end.setDate(end.getDate() + 7);
                  setStartTime(toLocalDatetimeString(now));
                  setEndTime(toLocalDatetimeString(end));
                  setNoEndDate(false);
                }}>
                  Next 7 days
                </button>
                <button className="pw-preset" onClick={() => {
                  const now = new Date();
                  const end = new Date();
                  end.setMonth(end.getMonth() + 1);
                  setStartTime(toLocalDatetimeString(now));
                  setEndTime(toLocalDatetimeString(end));
                  setNoEndDate(false);
                }}>
                  Next 30 days
                </button>
                <button className="pw-preset" onClick={() => {
                  const now = new Date();
                  now.setHours(9, 0, 0, 0);
                  const end = new Date();
                  end.setHours(17, 0, 0, 0);
                  if (end < new Date()) end.setDate(end.getDate() + 1);
                  setStartTime(toLocalDatetimeString(now));
                  setEndTime(toLocalDatetimeString(end));
                  setNoEndDate(false);
                }}>
                  Business hours
                </button>
              </div>
              <div className="pw-schedule-grid">
                <label className="pw-field">
                  <span>Start Date & Time</span>
                  <input
                    type="datetime-local"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                  />
                </label>
                <div className="pw-field">
                  <div className="pw-end-date-header">
                    <span>End Date & Time</span>
                    <label className="pw-toggle-label">
                      <input
                        type="checkbox"
                        checked={noEndDate}
                        onChange={(e) => setNoEndDate(e.target.checked)}
                      />
                      <span>Run indefinitely</span>
                    </label>
                  </div>
                  {!noEndDate && (
                    <input
                      type="datetime-local"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                    />
                  )}
                  {noEndDate && (
                    <div className="pw-no-end-note">Content will play until manually removed.</div>
                  )}
                </div>
                <label className="pw-field">
                  <span>Duration per cycle (seconds)</span>
                  <input
                    type="number"
                    min="5"
                    max="3600"
                    value={duration}
                    onChange={(e) => setDuration(parseInt(e.target.value) || 15)}
                  />
                </label>
                <div className="pw-field">
                  <span>Priority</span>
                  <div className="pw-priority-options">
                    <button
                      className={`pw-priority-btn ${priority === 100 ? "selected" : ""}`}
                      onClick={() => setPriority(100)}
                    >
                      <strong>Normal</strong>
                      <span>Default</span>
                    </button>
                    <button
                      className={`pw-priority-btn ${priority === 200 ? "selected" : ""}`}
                      onClick={() => setPriority(200)}
                    >
                      <strong>High</strong>
                      <span>Override other events on schedule</span>
                    </button>
                    <button
                      className={`pw-priority-btn ${priority === 300 ? "selected" : ""}`}
                      onClick={() => setPriority(300)}
                    >
                      <strong>Urgent</strong>
                      <span>Immediately override high priority events</span>
                    </button>
                  </div>
                </div>

              </div>

              {/* App Variables */}
              <div className="pw-appvars">
                <div className="pw-appvars-header">
                  <span className="pw-preset-label">App Variables</span>
                  <span className="pw-appvars-hint">Passed as query-string parameters to your app</span>
                </div>
                {appVars.map((v, i) => (
                  <div key={i} className="pw-appvar-row">
                    <input
                      className="pw-appvar-input"
                      placeholder="name"
                      value={v.name}
                      onChange={(e) => {
                        const updated = [...appVars];
                        updated[i] = { ...v, name: e.target.value };
                        setAppVars(updated);
                      }}
                    />
                    <span className="pw-appvar-eq">=</span>
                    <input
                      className="pw-appvar-input pw-appvar-value"
                      placeholder="value"
                      value={v.value}
                      onChange={(e) => {
                        const updated = [...appVars];
                        updated[i] = { ...v, value: e.target.value };
                        setAppVars(updated);
                      }}
                    />
                    <button
                      className="pw-appvar-remove"
                      onClick={() => setAppVars(appVars.filter((_, j) => j !== i))}
                    >×</button>
                  </div>
                ))}
                <div className="pw-appvar-row">
                  <input
                    className="pw-appvar-input"
                    placeholder="name"
                    value={newVarName}
                    onChange={(e) => setNewVarName(e.target.value)}
                  />
                  <span className="pw-appvar-eq">=</span>
                  <input
                    className="pw-appvar-input pw-appvar-value"
                    placeholder="value"
                    value={newVarValue}
                    onChange={(e) => setNewVarValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && newVarName.trim()) {
                        setAppVars([...appVars, { name: newVarName.trim(), value: newVarValue.trim() }]);
                        setNewVarName("");
                        setNewVarValue("");
                      }
                    }}
                  />
                  <button
                    className="pw-appvar-add"
                    disabled={!newVarName.trim()}
                    onClick={() => {
                      if (newVarName.trim()) {
                        setAppVars([...appVars, { name: newVarName.trim(), value: newVarValue.trim() }]);
                        setNewVarName("");
                        setNewVarValue("");
                      }
                    }}
                  >+ Add</button>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Confirm */}
          {step === 4 && !published && (
            <div className="pw-section">
              <div className="pw-summary">
                <div className="pw-summary-row">
                  <span>App</span>
                  <strong>{integration.name}</strong>
                </div>
                <div className="pw-summary-row">
                  <span>APK</span>
                  <strong>Forge Studio Player (from CMS)</strong>
                </div>
                <div className="pw-summary-row" style={{ opacity: 0.7, fontSize: "0.85em" }}>
                  <span></span>
                  <em>No build needed — schedules instantly</em>
                </div>
                <div className="pw-summary-row">
                  <span>Workspace</span>
                  <strong>{selectedWorkspace?.name}</strong>
                </div>
                <div className="pw-summary-row">
                  <span>Devices</span>
                  <strong>{selectedDevices.length} device{selectedDevices.length !== 1 ? "s" : ""}</strong>
                </div>
                <div className="pw-summary-row">
                  <span>Schedule</span>
                  <strong>
                    {new Date(startTime).toLocaleString()} &mdash;{" "}
                    {noEndDate ? "No end date" : new Date(endTime).toLocaleString()}
                  </strong>
                </div>
                <div className="pw-summary-row">
                  <span>Duration</span>
                  <strong>{duration}s per cycle</strong>
                </div>
                <div className="pw-summary-row">
                  <span>Priority</span>
                  <strong>{priority >= 300 ? "Urgent" : priority >= 200 ? "High" : "Normal"}</strong>
                </div>
                {/* A/B testing — coming in future release */
                false && (
                  <div className="pw-summary-row">
                    <span>A/B Test</span>
                    <strong>Coming soon</strong>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 4 && published && (
            <div className="pw-success">
              <div className="pw-success-icon">&#10003;</div>
              <h3>Published Successfully</h3>
              <p>
                Your app is now scheduled on {selectedDevices.length} device
                {selectedDevices.length !== 1 ? "s" : ""}.
              </p>
              <p style={{ fontSize: "0.9em", color: "#888", marginTop: "12px" }}>
                💡 <strong>Tip:</strong> If you edit this app later, use <strong>"Push to Devices"</strong> in My Apps to update all devices instantly — no rebuild needed.
              </p>
            </div>
          )}
        </div>

        {/* Footer buttons */}
        <div className="pw-footer">
          {step > 1 && !published && (
            <button className="pw-btn-back" onClick={() => setStep((s) => s - 1)}>
              Back
            </button>
          )}
          <div className="pw-footer-right">
            {step < 4 && (
              <button
                className="pw-btn-next"
                onClick={() => setStep((s) => s + 1)}
                disabled={!canNext()}
              >
                Next
              </button>
            )}
            {step === 4 && !published && (
              <button className="pw-btn-publish" onClick={handlePublish} disabled={publishing}>
                {publishing ? "Publishing..." : "Publish"}
              </button>
            )}
            {published && (
              <button className="pw-btn-next" onClick={onClose}>
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
